import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {
  projectCampaignRuntime,
  selectCampaignViewModel,
} from '../domain/campaignRuntime';
import {normalizeCampaignSnapshot} from '../domain/campaignSnapshot';
import {buildInitialQueueSteps, buildRegenStrategyQueueSteps} from './campaignSocket/constants';
import useCampaignSocket from './useCampaignSocket';

/**
 * Unified runtime hook: snapshot + transcript + live overlay → view model.
 */
export default function useCampaignRuntime({
  userAccountId,
  activeSessionId = null,
  sessionDetail,
  transcript,
  pendingSubmissions = {},
  localDraft = {},
  chatTransportStatus = 'idle',
}) {
  const {t} = useTranslation();
  const socket = useCampaignSocket(userAccountId, activeSessionId);

  const strategyQueueStepsTemplate = useMemo(
    () => buildInitialQueueSteps(t),
    [t],
  );

  const strategyRegenQueueStepsTemplate = useMemo(
    () => buildRegenStrategyQueueSteps(t),
    [t],
  );

  const formatArtStepLabel = useMemo(
    () => (pieceNumber) =>
      t('campaignChat.artProgress.piece', {
        defaultValue: `Arte ${pieceNumber}`,
        pieceNumber,
      }),
    [t],
  );

  const snapshot = useMemo(
    () => normalizeCampaignSnapshot(sessionDetail),
    [sessionDetail],
  );

  const strategyDocumentEditTitle = useMemo(
    () =>
      t(
        'campaignChat.strategyProgress.editingDocument',
        'Editando documento de estratégia...',
      ),
    [t],
  );

  const runtime = useMemo(
    () =>
      projectCampaignRuntime({
        snapshot,
        sessionDetail,
        transcript,
        liveOverlay: socket.liveOverlay,
        pendingSubmissions,
        chatTransportStatus,
        localDraft,
        strategyQueueStepsTemplate,
        strategyRegenQueueStepsTemplate,
        formatArtStepLabel,
        strategyProgressTitle: t('chatsocket.chat.creating_strategy'),
        strategyDocumentEditTitle,
        artProgressTitle: t('campaignChat.artProgress.generating', 'Gerando artes...'),
        artProgressEditingTitle: t(
          'campaignChat.chat.editing_arts',
          'Editando artes...',
        ),
      }),
    [
      snapshot,
      transcript,
      socket.liveOverlay,
      pendingSubmissions,
      chatTransportStatus,
      localDraft,
      strategyQueueStepsTemplate,
      strategyRegenQueueStepsTemplate,
      formatArtStepLabel,
      strategyDocumentEditTitle,
      t,
    ],
  );

  const viewModel = useMemo(
    () =>
      selectCampaignViewModel({
        snapshot,
        sessionDetail,
        transcript,
        liveOverlay: socket.liveOverlay,
        pendingSubmissions,
        localDraft,
        strategyQueueStepsTemplate,
        strategyRegenQueueStepsTemplate,
        formatArtStepLabel,
        chatTransportStatus,
        strategyProgressTitle: t('chatsocket.chat.creating_strategy'),
        strategyDocumentEditTitle,
        artProgressTitle: t('campaignChat.artProgress.generating', 'Gerando artes...'),
        artProgressEditingTitle: t(
          'campaignChat.chat.editing_arts',
          'Editando artes...',
        ),
      }),
    [
      snapshot,
      transcript,
      socket.liveOverlay,
      pendingSubmissions,
      localDraft,
      sessionDetail,
      strategyQueueStepsTemplate,
      strategyRegenQueueStepsTemplate,
      formatArtStepLabel,
      chatTransportStatus,
      strategyDocumentEditTitle,
      t,
    ],
  );

  return {
    ...socket,
    snapshot,
    runtime,
    viewModel,
  };
}
